import React from 'react';
import { X, Twitter, AlertCircle, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSocialAccounts } from '@/hooks/useSocialAccounts';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface PlatformSelectorProps {
  selectedPlatform: string;
  onChange: (platform: string) => void;
}

export const PlatformSelector: React.FC<PlatformSelectorProps> = ({ 
  selectedPlatform, 
  onChange 
}) => {
  const { accounts, isPlatformConnected } = useSocialAccounts();

  const handleChange = (platform: string) => {
    if (isPlatformConnected(platform)) {
      onChange(platform);
    }
  };

  const renderPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'x':
        return <X className="h-4 w-4" />;
      default:
        return <Twitter className="h-4 w-4" />;
    }
  };

  const getPlatformName = (platform: string) => {
    switch (platform) {
      case 'x':
        return 'X (Twitter)';
      default:
        return platform.charAt(0).toUpperCase() + platform.slice(1);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Platform</Label>
        <Link 
          to="/profile" 
          className="text-xs text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 flex items-center gap-1"
        >
          <ExternalLink className="h-3 w-3" />
          Manage Accounts
        </Link>
      </div>

      <RadioGroup value={selectedPlatform} onValueChange={handleChange} className="flex gap-2 flex-wrap">
        {accounts.map((account) => (
          <TooltipProvider key={account.platform}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div 
                  className={`
                    relative flex items-center gap-2 p-2 border rounded-md cursor-pointer
                    ${account.is_connected 
                      ? 'border-indigo-400 dark:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-950' 
                      : 'border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 opacity-60 cursor-not-allowed'}
                    ${selectedPlatform === account.platform ? 'bg-indigo-50 dark:bg-indigo-950' : ''}
                  `}
                  onClick={() => handleChange(account.platform)}
                >
                  <RadioGroupItem 
                    value={account.platform} 
                    id={account.platform}
                    disabled={!account.is_connected}
                    className="mr-1"
                  />
                  {renderPlatformIcon(account.platform)}
                  <span>{getPlatformName(account.platform)}</span>
                  
                  {!account.is_connected && (
                    <AlertCircle className="h-3 w-3 text-amber-500 ml-1" />
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                {account.is_connected ? (
                  <>Connected as @{account.account_username}</>
                ) : (
                  <>Not connected. Go to Profile → Connected Accounts to connect your {getPlatformName(account.platform)} account.</>
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ))}
      </RadioGroup>
    </div>
  );
}; 